// Simple logging utility with error tracking

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogOptions {
  component?: string;
  context?: Record<string, any>;
  userReported?: boolean;
  timestamp?: Date;
  tags?: string[];
}

export interface ErrorLog {
  error: Error;
  component?: string;
  context?: Record<string, any>;
  userReported?: boolean;
  timestamp: Date;
  tags?: string[];
}

// Interface for tracking sitemap URL crawling
export interface SitemapUrlLog {
  url: string;
  success: boolean;
  timestamp: Date;
  errorMessage?: string;
  statusCode?: number;
  responseSize?: number;
}

// Type for aggregated sitemap stats
export interface SitemapStats {
  total: number;
  successful: number;
  failed: number;
  successRate: number;
  failedUrls: Array<{url: string, error: string}>;
  statusCodes: Record<string, number>;
  averageResponseSize: number;
  crawlTime: number;
}

class Logger {
  private static instance: Logger;
  private errors: ErrorLog[] = [];
  private logLevel: LogLevel = 'info'; // Default log level
  private isDebugMode: boolean = false;
  private sitemapUrlLogs: SitemapUrlLog[] = []; // Track sitemap URL crawling
  private crawlStartTime: number = 0;
  
  private constructor() {}
  
  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }
  
  public setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }
  
  public enableDebugMode(): void {
    this.isDebugMode = true;
    this.logLevel = 'debug';
    console.info('Debug mode enabled for logger');
    this.crawlStartTime = Date.now();
  }
  
  public disableDebugMode(): void {
    this.isDebugMode = false;
    this.logLevel = 'info';
    console.info('Debug mode disabled for logger');
  }
  
  // New method to track sitemap URL crawl attempts
  public logSitemapUrlCrawl(url: string, success: boolean, options?: {
    errorMessage?: string;
    statusCode?: number;
    responseSize?: number;
  }): void {
    const { errorMessage, statusCode, responseSize } = options || {};
    
    this.sitemapUrlLogs.push({
      url,
      success,
      timestamp: new Date(),
      errorMessage,
      statusCode,
      responseSize
    });
    
    const status = success ? 'SUCCESS' : 'FAILED';
    const errorDetail = errorMessage ? ` - Error: ${errorMessage}` : '';
    const statusDetail = statusCode ? ` - Status: ${statusCode}` : '';
    const sizeDetail = responseSize ? ` - Size: ${responseSize} bytes` : '';
    
    this.debug(`Sitemap URL crawl ${status}: ${url}${errorDetail}${statusDetail}${sizeDetail}`, {
      component: 'CrawlerService',
      tags: ['crawler', 'sitemap', success ? 'success' : 'error'],
      context: { url, success, errorMessage, statusCode, responseSize }
    });
  }
  
  // Get sitemap URL crawl statistics with more detailed information
  public getSitemapUrlStats(): SitemapStats {
    const total = this.sitemapUrlLogs.length;
    const successful = this.sitemapUrlLogs.filter(log => log.success).length;
    const failed = total - successful;
    const successRate = total > 0 ? (successful / total) * 100 : 0;
    
    // Get detailed failure information
    const failedUrls = this.sitemapUrlLogs
      .filter(log => !log.success)
      .map(log => ({
        url: log.url,
        error: log.errorMessage || 'Unknown error'
      }));
    
    // Analyze status codes
    const statusCodes: Record<string, number> = {};
    this.sitemapUrlLogs.forEach(log => {
      if (log.statusCode) {
        const code = log.statusCode.toString();
        statusCodes[code] = (statusCodes[code] || 0) + 1;
      }
    });
    
    // Calculate average response size
    let totalSize = 0;
    let sizeCount = 0;
    this.sitemapUrlLogs.forEach(log => {
      if (log.responseSize) {
        totalSize += log.responseSize;
        sizeCount++;
      }
    });
    const averageResponseSize = sizeCount > 0 ? totalSize / sizeCount : 0;
    
    // Calculate crawl time
    const crawlTime = (Date.now() - this.crawlStartTime) / 1000;
    
    return {
      total,
      successful,
      failed,
      successRate,
      failedUrls,
      statusCodes,
      averageResponseSize,
      crawlTime
    };
  }
  
  // Clear sitemap URL logs
  public clearSitemapUrlLogs(): void {
    this.sitemapUrlLogs = [];
    this.crawlStartTime = Date.now();
  }
  
  private shouldLog(level: LogLevel): boolean {
    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3
    };
    
    return levels[level] >= levels[this.logLevel];
  }
  
  private formatMessage(level: LogLevel, message: string, options?: LogOptions): string {
    const timestamp = new Date().toISOString();
    const component = options?.component ? `[${options.component}]` : '';
    const tags = options?.tags?.length ? `[${options.tags.join(',')}]` : '';
    return `${timestamp} ${level.toUpperCase()} ${component} ${tags} ${message}`;
  }
  
  debug(message: string, options?: LogOptions): void {
    if (!this.shouldLog('debug')) return;
    console.debug(this.formatMessage('debug', message, options), options?.context || '');
  }
  
  info(message: string, options?: LogOptions): void {
    if (!this.shouldLog('info')) return;
    console.info(this.formatMessage('info', message, options), options?.context || '');
  }
  
  warn(message: string, options?: LogOptions): void {
    if (!this.shouldLog('warn')) return;
    console.warn(this.formatMessage('warn', message, options), options?.context || '');
  }
  
  error(message: string, error?: Error, options?: LogOptions): void {
    if (!this.shouldLog('error')) return;
    console.error(this.formatMessage('error', message, options), error, options?.context || '');
    
    // Track error for later analysis
    if (error) {
      this.errors.push({
        error,
        component: options?.component,
        context: options?.context,
        userReported: options?.userReported || false,
        timestamp: options?.timestamp || new Date(),
        tags: options?.tags
      });
    }
  }
  
  // Get all tracked errors
  getErrors(): ErrorLog[] {
    return [...this.errors];
  }
  
  // Get errors filtered by component
  getErrorsByComponent(component: string): ErrorLog[] {
    return this.errors.filter(err => err.component === component);
  }
  
  // Get errors filtered by tag
  getErrorsByTag(tag: string): ErrorLog[] {
    return this.errors.filter(err => err.tags?.includes(tag));
  }
  
  // Get user reported errors
  getUserReportedErrors(): ErrorLog[] {
    return this.errors.filter(err => err.userReported);
  }
  
  // Get recent errors
  getRecentErrors(count: number = 10): ErrorLog[] {
    return [...this.errors]
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, count);
  }
  
  // Clear error history
  clearErrors(): void {
    this.errors = [];
  }
  
  // Clear specific errors by component
  clearErrorsByComponent(component: string): void {
    this.errors = this.errors.filter(err => err.component !== component);
  }
  
  // Clear specific errors by tag
  clearErrorsByTag(tag: string): void {
    this.errors = this.errors.filter(err => !err.tags?.includes(tag));
  }
}

// Export a singleton instance
export const logger = Logger.getInstance();

// Helper function for component errors
export function logComponentError(
  componentName: string, 
  error: Error, 
  context?: Record<string, any>
): void {
  logger.error(`Error in ${componentName}`, error, {
    component: componentName,
    context
  });
}

// Helper function for crawler operations
export function logCrawlerOperation(
  operationType: string,
  message: string,
  context?: Record<string, any>
): void {
  logger.info(message, {
    component: 'CrawlerService',
    tags: ['crawler', operationType],
    context
  });
}

// Helper function for crawler errors
export function logCrawlerError(
  operationType: string,
  message: string,
  error: Error,
  context?: Record<string, any>
): void {
  logger.error(message, error, {
    component: 'CrawlerService',
    tags: ['crawler', operationType, 'error'],
    context
  });
}

// Helper function for crawler warnings
export function logCrawlerWarning(
  operationType: string,
  message: string,
  context?: Record<string, any>
): void {
  logger.warn(message, {
    component: 'CrawlerService',
    tags: ['crawler', operationType, 'warning'],
    context
  });
}

// Helper function for crawler debug info
export function logCrawlerDebug(
  operationType: string,
  message: string,
  context?: Record<string, any>
): void {
  logger.debug(message, {
    component: 'CrawlerService',
    tags: ['crawler', operationType, 'debug'],
    context
  });
}

// Helper function for sitemap URL crawl tracking with more detailed information
export function logSitemapUrlCrawl(
  url: string, 
  success: boolean, 
  options?: {
    errorMessage?: string;
    statusCode?: number;
    responseSize?: number;
  }
): void {
  logger.logSitemapUrlCrawl(url, success, options);
}

// New helper function to get sitemap crawl statistics
export function getSitemapCrawlStats(): SitemapStats {
  return logger.getSitemapUrlStats();
}

// New helper function to clear sitemap URL logs
export function clearSitemapUrlLogs(): void {
  logger.clearSitemapUrlLogs();
}
